import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kognitos/lattice';
import { V1AutomationStage } from '@/generated/hooks/grimoire/models';

/**
 * Filter options for version history stage filter.
 */
const STAGE_FILTER_OPTIONS = [
  {
    value: V1AutomationStage.AUTOMATION_STAGE_UNSPECIFIED,
    labelKey: 'automation.versionHistory.filter.all',
  },
  {
    value: V1AutomationStage.AUTOMATION_STAGE_PUBLISHED,
    labelKey: 'automation.versionHistory.filter.published',
  },
  {
    value: V1AutomationStage.AUTOMATION_STAGE_DRAFT,
    labelKey: 'automation.versionHistory.filter.drafts',
  },
] as const;

/**
 * Props for the FilterDropdownMenu component.
 */
export interface IFilterDropdownMenuProps {
  filter: V1AutomationStage;
  onFilterChange: (filter: V1AutomationStage) => void;
}

/**
 * Select menu for filtering version history by stage (All, Published, Drafts).
 *
 * @param props - Component props
 * @returns Filter select menu component
 *
 * @example
 * <FilterDropdownMenu
 *   filter={V1AutomationStage.AUTOMATION_STAGE_UNSPECIFIED}
 *   onFilterChange={handleFilterChange}
 * />
 */
export function VersionHistoryStageFilter({
  filter,
  onFilterChange,
}: IFilterDropdownMenuProps) {
  const { t } = useTranslation('automation');

  return (
    <Select
      value={filter}
      onValueChange={value => onFilterChange(value as V1AutomationStage)}
    >
      <SelectTrigger
        size="sm"
        className="w-auto"
        data-testid="version-history-filter-trigger"
        aria-label={t('automation.versionHistory.filter.label')}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end">
        {STAGE_FILTER_OPTIONS.map(option => (
          <SelectItem key={option.value} value={option.value}>
            {t(option.labelKey)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
